/**
 * NewAppointmentDialog
 *
 * Form for creating an appointment. Used in two trigger modes:
 *  - Click-and-drag on the Gantt grid: door + start + end pre-filled
 *  - Toolbar button: nothing pre-filled
 *
 * Submit calls POST /api/appointments. On success, parent gets the new
 * appointment back via onCreated and the dialog closes. On 409 conflict,
 * the conflict reasons display inline at the top of the form.
 */
import { useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { AlertCircle, Loader2 } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { PoFinder } from '@/components/PoFinder';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    ApiCallError,
    createAppointment,
    createBlockBatch,
    notifyAppointment,
    adminListCarrierDrivers,
    addAppointmentDriver,
    type CarrierDriverAdminRow,
    type BlockBatchResult,
} from '@/api/client';
import type { AppointmentDto, CalendarDoorDto, LookupsDto } from '@/api/types';
import { cn, NO_SELECTION } from '@/lib/utils';

interface PrefillInfo {
    dockDoorId: number | null;
    /**
     * ⭐ CARRIED OVER FROM A DRIVER CHECK-IN, when the appointment is being
     * created FOR an arrival already standing at the gate. The scheduler has a
     * card on screen with the driver, his carrier and whatever number he handed
     * over; retyping it into a blank form is slow and a chance to get it wrong.
     * All of it is a STARTING POINT the scheduler can still edit.
     */
    carrierCode?: string | null;
    poNumber?: string | null;
    referenceNumber?: string | null;
    driverName?: string | null;
    driverPhone?: string | null;
    /** ISO datetime, the local-time start the user dragged on */
    startLocal: string;
    /** ISO datetime, the local-time end the user dragged on */
    endLocal: string;
    /**
     * Opens the dialog straight into Block Time for one door.
     *
     * Blocking a door is REACTIVE work: a leveler drops at 10:40 and the door
     * has to go dark now, from the screen the scheduler is already looking at.
     * Making them open New Appointment, find the direction dropdown and switch
     * it to BLOCK is three decisions too many for that moment, and the blocks
     * that never get entered are the ones that cost a truck backing into a dead
     * door. Right-clicking the door row now lands here with the door and the
     * time already filled in.
     */
    direction?: 'INBOUND' | 'OUTBOUND' | 'BLOCK';
}

interface NewAppointmentDialogProps {
    open: boolean;
    onClose: () => void;
    facilityCode: string;
    doors: CalendarDoorDto[];
    lookups: LookupsDto;
    /** Defaults for the form. dockDoorId=null means "unassigned bucket". */
    prefill: PrefillInfo | null;
    /** Facility SLOT MIN (minutes): default + minimum appointment length. */
    slotMinutes?: number;
    /** Called with the created appointment so caller can update its view */
    onCreated: (appt: AppointmentDto) => void;
    /** Called after a multi-door Block Time batch is created. */
    onBlocksCreated?: (result: BlockBatchResult) => void;
    getToken: () => Promise<string>;
}

export function NewAppointmentDialog({
    open,
    onClose,
    facilityCode,
    doors,
    lookups,
    prefill,
    slotMinutes = 60,
    onCreated,
    onBlocksCreated,
    getToken,
}: NewAppointmentDialogProps) {
    // ---- Form state ----
    /**
     * ⛔⛔ AN EMPTY STRING IS NOT A TIME, AND `??` CANNOT TELL THE DIFFERENCE.
     *
     * ⚠️ `Calendar.tsx:handleCreateFromCheckIn` passes
     * `{ dockDoorId: null, startLocal: '', endLocal: '', ...prefillFromCheckIn(c) }`
     * — so `prefill?.startLocal ?? defaultStartLocal()` returned `''` and the
     * driver-card path opened with **blank date AND time fields**, while the
     * toolbar's `+ New appointment` (which passes `null`) prefilled correctly.
     * ⛔ **Two doors into one dialog, different behaviour, and the difference was
     * three empty strings.**
     *
     * ⭐ Pete, 2026-09-13: *"When you click new appointment from Driver, it should
     * have the current date for start and end dates so you only have to select the
     * time."* ⚠️ That REVERSES the call site's *"No door or time here: the
     * scheduler is still choosing those"* — a deliberate choice, changed
     * deliberately.
     *
     * ⭐⭐ AND THE FIX LIVES HERE RATHER THAN AT THE CALL SITE ON PURPOSE. The rule
     * *"a new appointment starts at the next slot boundary"* belongs to the dialog
     * that owns the fields. ⛔ Fixed in `Calendar.tsx` instead, the next caller
     * that spreads an empty string reintroduces it — and one already did.
     */
    // ⚠️ `orDefault` is module-scope (below, with the other helpers) so it is not
    //    a new identity every render and the hook dep arrays stay honest.
    const defaultStart = useMemo(
        () => orDefault(prefill?.startLocal, () => defaultStartLocal(slotMinutes)),
        [prefill, slotMinutes],
    );
    const defaultEnd = useMemo(
        () => orDefault(prefill?.endLocal, () => defaultEndLocal(defaultStart, slotMinutes)),
        [prefill, defaultStart, slotMinutes],
    );

    const [dockDoorId, setDockDoorId] = useState<number | null>(prefill?.dockDoorId ?? null);
    // Block Time can target multiple doors at once (e.g. "all inbound doors").
    const [blockDoorIds, setBlockDoorIds] = useState<number[]>(
        prefill?.dockDoorId != null ? [prefill.dockDoorId] : [],
    );
    const [direction, setDirection] = useState<'INBOUND' | 'OUTBOUND' | 'BLOCK'>('INBOUND');
    const [carrierCode, setCarrierCode] = useState<string>('');
    /**
     * ---- ⛔⛤ DEFAULT **OFF** AS OF 2026-09-18 ----
     *
     * > Pete: *"Both should default to not checked"* — the carrier box as well as
     * > the new client one.
     *
     * ⛔⛤ ~~default on~~. It shipped checked, so every appointment created without
     * a second thought emailed the carrier. ⭐ Sending is now a decision someone
     * makes, not one they forget to undo.
     *
     *   ** A CHECKED-BY-DEFAULT BOX THAT SENDS MAIL IS NOT A DEFAULT, IT IS AN
     *      OPT-OUT NOBODY READS. **
     */
    const [notifyCarrier, setNotifyCarrier] = useState<boolean>(false);
    const [clientCode, setClientCode] = useState<string>('');
    const [poNumber, setPoNumber] = useState<string>('');
    // #130. ⚠️ No trailer number here on purpose: a driver arrives with that, so a
    // booking form asking for it invites a guess the back-fill would then argue
    // with. See BOOKABLE_FIELDS on the server.
    const [containerNumber, setContainerNumber] = useState<string>('');
    const [referenceNumber, setReferenceNumber] = useState<string>('');
    const [palletCount, setPalletCount] = useState<string>('');
    // Outbound only — at least one row required for outbound. Each row is
    // a consignee + optional shipment#. Multi-row is rare (LTL consolidation).
    const [consignees, setConsignees] = useState<
        { consigneeCode: string; shipmentNumber: string }[]
    >([{ consigneeCode: '', shipmentNumber: '' }]);
    const [startLocal, setStartLocal] = useState<string>(defaultStart);
    const [endLocal, setEndLocal] = useState<string>(defaultEnd);
    const [comments, setComments] = useState<string>('');
    const [capabilityIds, setCapabilityIds] = useState<number[]>([]);

    // Drivers to attach at creation (optional). Staged locally; fired as
    // best-effort POST .../drivers calls AFTER the appointment is created (we
    // need the new appointmentId first). Each staged entry is either a roster
    // pick (driverId set) or a brand-new driver (name/phone/email). Scoped to
    // the selected carrier; changing carrier clears the list.
    type StagedNewDriver = { driverId: number | null; name: string; phone: string; email?: string | null };
    const [stagedDrivers, setStagedDrivers] = useState<StagedNewDriver[]>([]);
    const [driverMode, setDriverMode] = useState<'existing' | 'new'>('existing');
    const [pendingRosterId, setPendingRosterId] = useState<number | null>(null);
    const [newDriverName, setNewDriverName] = useState('');
    const [newDriverPhone, setNewDriverPhone] = useState('');
    const [newDriverEmail, setNewDriverEmail] = useState('');
    const [driverRoster, setDriverRoster] = useState<
        CarrierDriverAdminRow[] | 'loading' | { error: string }
    >('loading');

    // PO finder
	const [poFinderOpen, setPoFinderOpen] = useState(false);

    // Submit state
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [conflicts, setConflicts] = useState<{ type: string; detail: string; severity?: 'error' | 'warning' }[]>([]);
    const [submitError, setSubmitError] = useState<string | null>(null);

    // Reset everything when the dialog is reopened with new prefill
    useEffect(() => {
        if (!open) return;
        setDockDoorId(prefill?.dockDoorId ?? null);
        setBlockDoorIds(prefill?.dockDoorId != null ? [prefill.dockDoorId] : []);
        setDirection(prefill?.direction ?? 'INBOUND');
        setCarrierCode(prefill?.carrierCode ?? '');
        setClientCode('');
        setPoNumber(prefill?.poNumber ?? '');
        setContainerNumber('');
        setReferenceNumber(prefill?.referenceNumber ?? '');
        setPalletCount('');
        setConsignees([{ consigneeCode: '', shipmentNumber: '' }]);
        // ⚠️ SAME EMPTY-STRING RULE AS THE INITIAL STATE, AND IT HAS TO BE. This
        //    runs on every open; if the two disagreed the dialog would prefill on
        //    mount and blank itself the second time it was opened.
        const s = orDefault(prefill?.startLocal, () => defaultStartLocal(slotMinutes));
        setStartLocal(s);
        setEndLocal(orDefault(prefill?.endLocal, () => defaultEndLocal(s, slotMinutes)));
        setComments('');
        setCapabilityIds([]);
        setStagedDrivers([]);
        setDriverMode('existing');
        setPendingRosterId(null);
        // A driver physically at the gate is a NEW driver as far as this form is
        // concerned -- he may not be on the carrier's roster, and making the
        // scheduler hunt for him there when his name is already on the card is
        // exactly the friction this removes.
        if (prefill?.driverName || prefill?.driverPhone) {
            setDriverMode('new');
            setNewDriverName(prefill.driverName ?? '');
            setNewDriverPhone(prefill.driverPhone ?? '');
        } else {
            setNewDriverName(''); setNewDriverPhone('');
        }
        setNewDriverEmail('');
        setSubmitting(false);
        setConflicts([]);
        setSubmitError(null);
    }, [open, prefill, slotMinutes]);

    // Load the selected carrier's driver roster (for pick-from-roster at
    // creation). Re-runs when the carrier changes; clears any staged drivers
    // since the roster is per-carrier. Skipped for BLOCK (no carrier/drivers).
    useEffect(() => {
        if (!open || !carrierCode) { setDriverRoster('loading'); setStagedDrivers([]); return; }
        let cancelled = false;
        setStagedDrivers([]);
        setPendingRosterId(null);
        setDriverRoster('loading');
        (async () => {
            try {
                const token = await getToken();
                const drivers = await adminListCarrierDrivers(token, carrierCode);
                if (!cancelled) setDriverRoster(drivers);
            } catch (e) {
                if (!cancelled) setDriverRoster({ error: e instanceof ApiCallError ? e.message : (e instanceof Error ? e.message : 'Failed to load roster.') });
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, carrierCode, getToken]);

    // ---- Capability toggling ----
    const toggleCapability = (id: number) => {
        setCapabilityIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    // ---- Driver staging (attached best-effort after create) ----

    /**
     * PICKING FROM THE ROSTER STAGES IMMEDIATELY — there is no "Add" step.
     *
     * The old flow needed a second click on an Add button next to the dropdown,
     * and a selected-looking driver that had not been staged is indistinguishable
     * from a staged one. That is not hypothetical: it is exactly how seven
     * carrier-portal bookings reached the dock with no driver attached
     * (2026-08-13). The carrier portal was fixed the same day; this screen was
     * left behind, so the same trap was still live for schedulers.
     *
     * The typed-in "Add new driver" path KEEPS its confirm button: a half-typed
     * name and phone are not a decision, and there is no moment we could call
     * "chosen" without one.
     */
    const stageRosterDriver = (driverId: number | null) => {
        setSubmitError(null);
        if (driverId == null) return;
        if (stagedDrivers.some((d) => d.driverId === driverId)) {
            setSubmitError('That driver is already added.');
            return;
        }
        const roster = Array.isArray(driverRoster) ? driverRoster : [];
        const picked = roster.find((d) => d.driverId === driverId);
        if (!picked) { setSubmitError('Selected driver not found in roster.'); return; }
        setStagedDrivers((prev) => [...prev, { driverId: picked.driverId, name: picked.name, phone: picked.phone }]);
        setPendingRosterId(null);
    };

    const stageAddDriver = () => {
        setSubmitError(null);
        if (driverMode === 'existing') {
            stageRosterDriver(pendingRosterId);
        } else {
            if (!newDriverName.trim() || !newDriverPhone.trim()) { setSubmitError('New driver needs a name and phone.'); return; }
            setStagedDrivers((prev) => [...prev, { driverId: null, name: newDriverName.trim(), phone: newDriverPhone.trim(), email: newDriverEmail.trim() || null }]);
            setNewDriverName(''); setNewDriverPhone(''); setNewDriverEmail('');
        }
    };
    const stageRemoveDriver = (index: number) => setStagedDrivers((prev) => prev.filter((_, i) => i !== index));

    // ---- Submit ----
    const handleSubmit = async (override = false) => {
        setSubmitError(null);
        setConflicts([]);

        // Client-side guards before hitting the API
        const isBlock = direction === 'BLOCK';
        if (isBlock) {
            if (blockDoorIds.length === 0) {
                setSubmitError('Select at least one door to block.');
                return;
            }
        } else {
            /**
             * ⛔ NO CARRIER CHECK. Pete, 2026-08-25: *"Carrier can't be required on the
             * create appt screen. Won't always know it"* — arch §1R.8: a brokered load
             * has no carrier when the schedule is made, so a blank one is CORRECT and
             * the server stores 'TBD'.
             *
             * ⚠️ THE CLIENT CHECK STAYS. dock.Appointment.ClientCode is NOT NULL and a
             * load with no client belongs to nobody — that one is a real requirement,
             * not an inherited habit.
             */
            if (!clientCode) {
                setSubmitError('Client is required.');
                return;
            }
            // ⛔ THE CLIENT-SIDE "PO REQUIRED" CHECK IS GONE (#129/#132).
            //
            // Pete, 2026-08-20: *"we should allow appointments to be created without
            // a PO / Receipt Number required."* Whether one is needed is now a
            // per-facility, per-scope, per-field policy (migration 151) that only
            // the server can answer — so the server answers it, with a 400 carrying
            // `code: 'required_fields_missing'` and a `missing[]` array.
            //
            // ⚠️ DO NOT re-add a check here "to save a round trip". A hardcoded copy
            // of the rule in the browser is a rule that ignores the configuration,
            // and it would refuse bookings at facilities that deliberately allow
            // them — silently, and only for users on this screen.
        }
        const palletNum = Number(palletCount);
        if (!isBlock && (palletCount.trim() === '' || !Number.isInteger(palletNum) || palletNum < 0)) {
            setSubmitError('Pallet count is required (a whole number, 0 or more).');
            return;
        }

        // Direction-specific validation
        let consigneesToSend: { consigneeCode: string; shipmentNumber: string | null }[] = [];
        if (direction === 'OUTBOUND') {
            const cleanedConsignees = consignees
                .map((c) => ({
                    consigneeCode: c.consigneeCode.trim(),
                    shipmentNumber: c.shipmentNumber.trim() || null,
                }))
                .filter((c) => c.consigneeCode.length > 0);
            /**
             * ⛔ THE EMPTY-CONSIGNEE REFUSAL WAS HERE AND IS GONE (2026-08-27).
             * Pete: *"Need to remove this requirement."* See the note in schemas.ts.
             *
             * ⚠️ `cleanedConsignees` MAY NOW BE EMPTY AND THAT IS ALLOWED — an empty
             * array is sent, never a row with a blank code.
             */
            consigneesToSend = cleanedConsignees;
        }

        const startUtc = localInputToIsoUtc(startLocal);
        const endUtc = localInputToIsoUtc(endLocal);
        if (!startUtc || !endUtc) {
            setSubmitError('Start and end times are required.');
            return;
        }
        if (new Date(endUtc) <= new Date(startUtc)) {
            setSubmitError('End time must be after start time.');
            return;
        }
        const durationMin = Math.round((new Date(endUtc).getTime() - new Date(startUtc).getTime()) / 60000);
        if (!isBlock && (durationMin < slotMinutes || durationMin % slotMinutes !== 0)) {
            setSubmitError(`Appointments must be at least ${slotMinutes} minutes and in ${slotMinutes}-minute increments (currently ${durationMin}).`);
            return;
        }

        setSubmitting(true);
        try {
            const token = await getToken();
            if (isBlock) {
                const result = await createBlockBatch(token, {
                    facilityCode,
                    dockDoorIds: blockDoorIds,
                    scheduledStartUtc: startUtc,
                    scheduledEndUtc: endUtc,
                    comments: comments.trim() || null,
                });
                onBlocksCreated?.(result);
                onClose();
                return;
            }
            const appt = await createAppointment(token, {
                facilityCode,
                dockDoorId,
                direction,
                referenceNumber: referenceNumber.trim() || null,
                scheduledStartUtc: startUtc,
                scheduledEndUtc: endUtc,
                comments: comments.trim() || null,
                requiredCapabilityIds: isBlock ? [] : capabilityIds,
                overrideWarnings: override,
                ...(isBlock ? {} : {
                    carrierCode,
                    clientCode,
                    documentType: direction === 'INBOUND' ? 'PO' : 'SHIPMENT',
                    poNumber: direction === 'INBOUND' ? poNumber.trim() || null : null,
                    // #130. The column has existed since migration 139 and nothing
                    // ever wrote it, so a facility could require a field no form
                    // could supply.
                    containerNumber: containerNumber.trim() || null,
                    palletCount: palletNum,
                    consignees: consigneesToSend.map((c, i) => ({
                        consigneeCode: c.consigneeCode,
                        shipmentNumber: c.shipmentNumber,
                        isPrimary: i === 0,
                    })),
                }),
            });
            // Attach any staged drivers to the just-created appointment.
            // Best-effort: the appointment already exists, so a driver-add
            // failure must NOT fail the whole create — collect failures and
            // surface them as a non-fatal warning instead.
            const driverFailures: string[] = [];
            for (const d of stagedDrivers) {
                try {
                    const body = d.driverId != null
                        ? { driverId: d.driverId }
                        : { name: d.name, phone: d.phone, email: d.email ?? null };
                    await addAppointmentDriver(token, appt.appointmentId, body);
                } catch {
                    driverFailures.push(d.name);
                }
            }
            // Optionally email the carrier that the appointment was scheduled for
            // them (default on). Best-effort — never block/fail the create on email.
            if (notifyCarrier && carrierCode) {
                try {
                    // targets is EXPLICIT even though 'created' doesn't text
                    // drivers today: the checkbox above says "notify carrier",
                    // so this call must mean carrier, and stay meaning that if
                    // 'created' is ever added to DRIVER_NOTIFY_EVENTS. Omitting
                    // targets means BOTH server-side.
                    await notifyAppointment(token, appt.appointmentId, 'created', ['carrier']);
                } catch {
                    /* swallow — the appointment was created; email is a courtesy */
                }
            }
            if (driverFailures.length > 0) {
                // Appointment WAS created — don't lose it. Warn about the
                // drivers that didn't attach; the user can add them from the
                // appointment's Drivers dialog. Notify the parent so the board
                // refreshes to show the new appointment, then surface the notice.
                onCreated(appt);
                setSubmitError(
                    `Appointment created, but couldn't add: ${driverFailures.join(', ')}. ` +
                    `Add them from the appointment's Drivers dialog.`,
                );
                setSubmitting(false);
                return;
            }
            onCreated(appt);
            onClose();
        } catch (err) {
            if (err instanceof ApiCallError) {
                if (err.details.conflicts && err.details.conflicts.length > 0) {
                    setConflicts(err.details.conflicts);
                } else {
                    setSubmitError(err.message);
                }
            } else {
                setSubmitError(errMsg(err));
            }
        } finally {
            setSubmitting(false);
        }
    };

    /**
     * ---- ⭐⭐ ONE REFERENCE FIELD, TWO PLACES (Pete, 2026-09-25) -------------
     *
     * > *"Inbound has the search po right after the carrier and client dropdowns
     * > but outbound has the find shipment further down. The screens should be
     * > built in the same structure to avoid confusion."*
     *
     * ⛔⚤ THIS RETIRES THE 2026-08-14 PLACEMENT, which argued the opposite and
     * argued it well: outbound schedulers start from a shipment number, so the
     * button belonged on the field they were already typing into. That is still
     * true of the FIELD. What it did not weigh is that the two directions then
     * disagree about WHERE THE SEARCH IS, and a scheduler switching between them
     * has to hunt for it.
     *   ** OPTIMISING EACH SCREEN SEPARATELY PRODUCES TWO GOOD SCREENS THAT ARE
     *      BAD AS A PAIR. **
     *
     * ⛔ HOISTED TO A CONST, NOT COPIED. It renders in one of two slots depending
     * on direction, and a second copy of forty lines of JSX would drift the moment
     * anybody edited one of them - which is precisely how the status palette came
     * to be wrong on three surfaces at once.
     *   ** RENDERING THE SAME ELEMENT IN TWO PLACES IS ONE ELEMENT. WRITING IT
     *      TWICE IS TWO. **
     */
    const referenceField = (
                <div className="col-span-2 sm:col-span-1">
                    {/* HINT GOES UNDER THE INPUT, NOT IN THE LABEL.
                        Appended to the label it wrapped to a second line, which
                        pushed this input a row lower than Pallet Count beside it
                        and made the pair look broken. Labels stay one line so the
                        two columns line up; the hint sits below where it costs
                        nothing. */}
                    <Label htmlFor="ref">Reference Number (optional)</Label>
                    {/* OUTBOUND GETS THE SEARCH HERE, not next to Consignee(s).
                        Pete, 2026-08-14: schedulers start from a shipment or
                        reference number, so the button belongs on the field
                        they are already typing into. (Inbound keeps its Find PO
                        beside the PO Number field, for the same reason.) */}
                    <div className="mt-1.5 flex gap-2">
                        <Input
                            id="ref"
                            value={referenceNumber}
                            onChange={(e) => setReferenceNumber(e.target.value)}
                            placeholder={direction === 'OUTBOUND'
                                ? 'Shipment #, BOL, trailer #'
                                : 'BOL, trailer #, internal reference'}
                        />
                        {/* variant="default" and no extra classes, so it is the
                            SAME button as Find PO on inbound. Two buttons doing
                            the same job should not look like different kinds of
                            action. (A JSX comment cannot sit inside `cond && (…)`
                            alongside the element — that is two expressions.) */}
                        {direction === 'OUTBOUND' && (
                            <Button
                                type="button"
                                variant="default"
                                onClick={() => setPoFinderOpen(true)}
                            >
                                Find shipment
                            </Button>
                        )}
                    </div>
                    {direction === 'OUTBOUND' && (
                        <p className="mt-1 text-xs text-muted-foreground">
                            Find shipment also searches order #, reference, code and client.
                        </p>
                    )}
                </div>
    );

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>New Appointment</DialogTitle>
                    <DialogDescription>
                        Schedule a truck arrival. Find PO / Find shipment searches every warehouse
                        system by number, reference, code or client.
                    </DialogDescription>
                </DialogHeader>

                <p className="text-xs text-muted-foreground -mt-2"><span className="text-destructive">*</span> required fields</p>

                {/* ---- Conflict / error banner ---- */}
                {(() => {
                    const warningsOnly = conflicts.length > 0 && conflicts.every((c) => c.severity === 'warning');
                    if (conflicts.length === 0 && !submitError) return null;
                    if (warningsOnly) {
                        const types = conflicts.map((c) => c.type);
                        const heading = types.includes('DoorDedicated')
                            ? 'Door reserved'
                            : types.includes('FacilityCapExceeded')
                                ? 'Over the facility limit'
                                : 'Heads up';
                        return (
                            <div className="rounded-md border border-amber-400/60 bg-amber-50 p-3 text-sm">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                                    <div className="flex-1">
                                        <div className="font-medium text-amber-800">{heading}</div>
                                        <ul className="mt-1 list-disc list-inside text-amber-800/80 space-y-0.5">
                                            {conflicts.map((c, i) => (
                                                <li key={i}>{c.detail}</li>
                                            ))}
                                        </ul>
                                        <div className="mt-2">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="border-amber-500 text-amber-800 hover:bg-amber-100"
                                                disabled={submitting}
                                                onClick={() => handleSubmit(true)}
                                            >
                                                {direction === 'BLOCK' ? 'Create block anyway' : 'Create anyway'}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    }
                    return (
                        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
                            <div className="flex items-start gap-2">
                                <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                                <div className="flex-1">
                                    {submitError && <div className="font-medium text-destructive">{submitError}</div>}
                                    {conflicts.length > 0 && (
                                        <>
                                            <div className="font-medium text-destructive">Cannot save:</div>
                                            <ul className="mt-1 list-disc list-inside text-muted-foreground space-y-0.5">
                                                {conflicts.map((c, i) => (
                                                    <li key={i}>
                                                        <span className="text-foreground">{c.detail}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {/* ---- Form grid ---- */}
                <div className="grid grid-cols-2 gap-4">
                    {/**
                      * ---- ⭐⭐ START AND END COME FIRST (Pete, 2026-09-25) ----------
                      *
                      * > *"I think Start and End dates should be the first thing, above
                      * > Dock Door and Direction line."*
                      *
                      * ⭐ IT IS THE FIRST DECISION ANYONE ACTUALLY MAKES. A scheduler
                      * books a TIME and then finds a door for it; the form asked for the
                      * door first and the time eleven fields later, near the bottom of a
                      * dialog that scrolls.
                      *   ** A FORM SHOULD ASK IN THE ORDER THE DECISION IS MADE, NOT IN
                      *      THE ORDER THE COLUMNS WERE ADDED TO THE TABLE. **
                      *
                      * ⚠️ THE EDIT DIALOG MOVES WITH IT, in the same release. Two forms
                      * over the same record asking in different orders is the exact
                      * inconsistency Pete raised in the same message.
                      */}
                    {/*
                      * Start / End
                      *
                      * ⭐⭐ `step` IS THE WHOLE FIX FOR THE TIME PICKER. Without it the
                      * browser's native control offers **every minute of the hour** in a
                      * scroll list — Pete, 2026-09-13: *"selecting time from this layout
                      * is very cumbersome."* With `step = slotMinutes * 60` it offers
                      * `:00`/`:30` at a 30-minute facility and `:00` only at an hourly one.
                      *
                      * ⭐ AND IT IS THE SAME NUMBER THE SUBMIT CHECK ALREADY ENFORCES.
                      * That check now becomes a **backstop** rather than the thing a
                      * scheduler discovers after fighting the picker: the input can no
                      * longer easily produce a value the dialog will reject.
                      *
                      * ⚠️ `step` counts from midnight, not from `now`, which is exactly
                      * what is wanted — the offered times line up with the Gantt grid the
                      * appointment will be drawn on.
                      *
                      * ⛔ IT IS NOT A VALIDATION. A pasted or typed off-grid value still
                      * reaches `handleSubmit`, and the duration check there is still the
                      * authority. A `step` treated as enforcement is a client-side rule,
                      * and §1M.4 does not let a client-side rule be the only one.
                      */}
                    <div className="col-span-2 sm:col-span-1">
                        <Label htmlFor="start">Start</Label>
                        <Input
                            id="start"
                            type="datetime-local"
                            step={slotMinutes * 60}
                            value={startLocal}
                            onChange={(e) => setStartLocal(e.target.value)}
                            className="mt-1.5 text-tabular"
                        />
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                        <Label htmlFor="end">End</Label>
                        <Input
                            id="end"
                            type="datetime-local"
                            step={slotMinutes * 60}
                            value={endLocal}
                            onChange={(e) => setEndLocal(e.target.value)}
                            className="mt-1.5 text-tabular"
                        />
                    </div>
                    {/* Door — single select for appointments; multi-pick for blocks */}
                    {direction === 'BLOCK' ? (
                        <div className="col-span-2">
                            <Label>Doors to block <span className="text-destructive">*</span></Label>
                            <div className="mt-1.5 flex flex-wrap gap-2 mb-2">
                                <Button type="button" size="sm" variant="outline"
                                    onClick={() => setBlockDoorIds(doors.map((d) => d.dockDoorId))}>
                                    All doors
                                </Button>
                                <Button type="button" size="sm" variant="outline"
                                    onClick={() => setBlockDoorIds(doors.filter((d) => d.allowedDirection === 'INBOUND' || d.allowedDirection === 'BOTH').map((d) => d.dockDoorId))}>
                                    All inbound
                                </Button>
                                <Button type="button" size="sm" variant="outline"
                                    onClick={() => setBlockDoorIds(doors.filter((d) => d.allowedDirection === 'OUTBOUND' || d.allowedDirection === 'BOTH').map((d) => d.dockDoorId))}>
                                    All outbound
                                </Button>
                                <Button type="button" size="sm" variant="ghost"
                                    onClick={() => setBlockDoorIds([])}>
                                    Clear
                                </Button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {doors.map((d) => {
                                    const on = blockDoorIds.includes(d.dockDoorId);
                                    return (
                                        <button
                                            type="button"
                                            key={d.dockDoorId}
                                            onClick={() => setBlockDoorIds((prev) =>
                                                on ? prev.filter((x) => x !== d.dockDoorId) : [...prev, d.dockDoorId])}
                                            className={cn(
                                                'px-3 py-1 rounded-full border text-xs font-medium transition-colors',
                                                on ? 'bg-primary text-primary-foreground border-primary'
                                                   : 'bg-background hover:bg-accent border-input',
                                            )}
                                        >
                                            {d.code}
                                            {d.allowedDirection !== 'BOTH' && (
                                                <span className="ml-1 opacity-70 text-[10px]">({d.allowedDirection.toLowerCase()})</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1.5">
                                {blockDoorIds.length} door{blockDoorIds.length === 1 ? '' : 's'} selected. Doors already booked in this window are skipped.
                            </p>
                        </div>
                    ) : (
                    <div className="col-span-2 sm:col-span-1">
                        <Label htmlFor="door">Dock Door</Label>
                        <Select
                            value={dockDoorId !== null ? String(dockDoorId) : 'UNASSIGNED'}
                            onValueChange={(v) => setDockDoorId(v === 'UNASSIGNED' ? null : Number(v))}
                        >
                            <SelectTrigger id="door" className="mt-1.5">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="UNASSIGNED">Unassigned (queue)</SelectItem>
                                {doors
                                    .filter(
                                        (d) =>
                                            d.allowedDirection === 'BOTH' ||
                                            d.allowedDirection === direction,
                                    )
                                    .map((d) => (
                                        <SelectItem key={d.dockDoorId} value={String(d.dockDoorId)}>
                                            {d.code} <span className="text-muted-foreground ml-1">· {d.name}</span>
                                            {d.allowedDirection !== 'BOTH' && (
                                                <span className="text-muted-foreground ml-1 text-xs">
                                                    ({d.allowedDirection.toLowerCase()})
                                                </span>
                                            )}
                                        </SelectItem>
                                    ))}
                            </SelectContent>
                        </Select>
                    </div>
                    )}

                    {/* Direction */}
                    <div className="col-span-2 sm:col-span-1">
                        <Label htmlFor="direction">Direction</Label>
                        <Select
                            value={direction}
                            onValueChange={(v) => {
                                setDirection(v as 'INBOUND' | 'OUTBOUND' | 'BLOCK');
                                // Reset door if its allowedDirection no longer matches.
                                // Blocks accept any door, so never reset for BLOCK.
                                const currentDoor = doors.find((d) => d.dockDoorId === dockDoorId);
                                if (v !== 'BLOCK' && currentDoor && currentDoor.allowedDirection !== 'BOTH' && currentDoor.allowedDirection !== v) {
                                    setDockDoorId(null);
                                }
                            }}
                        >
                            <SelectTrigger id="direction" className="mt-1.5">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="INBOUND">Inbound (receiving)</SelectItem>
                                <SelectItem value="OUTBOUND">Outbound (shipping)</SelectItem>
                                <SelectItem value="BLOCK">Block Time (out of service)</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Carrier + Client (hidden for blocks) */}
                    {direction !== 'BLOCK' && (
                    <>
                    <div className="col-span-2 sm:col-span-1">
                        <Label htmlFor="carrier">Carrier (optional)</Label>
                        <Select value={carrierCode || NO_SELECTION}
                                onValueChange={(v) => setCarrierCode(v === NO_SELECTION ? '' : v)}>
                            <SelectTrigger id="carrier" className="mt-1.5">
                                <SelectValue placeholder="Select carrier" />
                            </SelectTrigger>
                            <SelectContent>
                                {/**
                                  * ⭐ AN EXPLICIT "NOT KNOWN YET" OPTION (2026-08-25).
                                  * Making the carrier optional in validation is not enough
                                  * if the control has no way to express it — a Select with
                                  * only real carriers in it means a scheduler who does not
                                  * know the carrier must either pick a wrong one or leave a
                                  * field that LOOKS unfinished.
                                  *
                                  * ⛔ IT CARRIES A SENTINEL, NOT THE EMPTY STRING — AND THIS
                                  * PARAGRAPH USED TO SAY THE OPPOSITE. `value=""` made Radix
                                  * throw DURING RENDER ("must have a value prop that is not an
                                  * empty string"), which took the whole dialog down to a white
                                  * screen rather than degrading. The empty string is how a
                                  * Select says "nothing chosen, show the placeholder", so it
                                  * cannot also BE a choice.
                                  *
                                  * ⛔ THE SENTINEL IS TRANSLATED AWAY AT THIS CONTROL, both
                                  * ways, so nothing new reaches the server: the API still
                                  * receives a blank carrier and still stores 'TBD' (arch
                                  * §1R.8). The option is a way to SAY it, not a new carrier.
                                  */}
                                <SelectItem value={NO_SELECTION}>Not known yet (TBD)</SelectItem>
                                {lookups.carriers.map((c) => (
                                    <SelectItem key={c.CarrierCode} value={c.CarrierCode}>
                                        {c.CarrierName}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Client */}
                    <div className="col-span-2 sm:col-span-1">
                        <Label htmlFor="client">Client</Label>
                        <Select value={clientCode} onValueChange={setClientCode}>
                            <SelectTrigger id="client" className="mt-1.5">
                                <SelectValue placeholder="Select client" />
                            </SelectTrigger>
                            <SelectContent>
                                {lookups.clients.map((c) => (
                                    <SelectItem key={c.ClientCode} value={c.ClientCode}>
                                        {c.ClientName}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    </>
                    )}

                    {/* PO Number + Find PO (inbound only).
                        ⚠️ NO LONGER MARKED REQUIRED. Whether it is required is a
                        per-facility, per-scope policy (migration 151) that this
                        screen cannot know without asking — and the common case Pete
                        described is precisely that it is NOT: the carrier has the
                        wrong number or none at all, and the scheduler finds it in
                        the WMS once the driver arrives. */}
                    {direction === 'INBOUND' && (
                        <div className="col-span-2">
                            {/* "PO / Receipt Number" because that is literally what
                                the field holds: picking a Camelot receipt puts the
                                RECEIPT number here (12605), not a PO. The old label
                                described half the values it stores. */}
                            <Label htmlFor="po">PO / Receipt Number</Label>
                            <div className="mt-1.5 flex gap-2">
                                <Input
                                    id="po"
                                    value={poNumber}
                                    onChange={(e) => setPoNumber(e.target.value)}
                                    placeholder="PO or receipt #"
                                    className="font-mono"
                                />
                                <Button
                                    type="button"
                                    variant="default"
                                    onClick={() => setPoFinderOpen(true)}
                                >
                                    Find PO
                                </Button>
                            </div>
                            <p className="mt-1 text-xs text-muted-foreground">
                                Find PO also searches receipt #, reference, code and client.
                                Leave it empty if you do not have it yet — it can be
                                filled in from the driver's paperwork when the truck arrives.
                            </p>
                        </div>
                    )}
                    {/* ⭐ THE OUTBOUND SEARCH SLOT, level with the inbound Find PO. */}
                    {direction === 'OUTBOUND' && referenceField}

                    {/* ⭐ CONTAINER NUMBER (#130). dock.Appointment.ContainerNumber
                        has existed since migration 139 and NOTHING has ever written
                        it — no schema field, no INSERT column, no input. So a
                        facility could configure "container number required" against a
                        field nobody could type. Shown for both directions: an
                        outbound load goes into a container just as an inbound one
                        comes out of it.
                        ⚠️ No trailer number field, deliberately — a driver arrives
                        with that. See BOOKABLE_FIELDS on the server. */}
                    {direction !== 'BLOCK' && (
                        <div className="col-span-2">
                            <Label htmlFor="container">Container Number</Label>
                            <Input
                                id="container"
                                className="mt-1.5 font-mono"
                                value={containerNumber}
                                onChange={(e) => setContainerNumber(e.target.value)}
                                placeholder="optional"
                            />
                        </div>
                    )}
					
					 <PoFinder
                        open={poFinderOpen}
                        onClose={() => setPoFinderOpen(false)}
                        getToken={getToken}
                        facilityCode={facilityCode}
                        anchorDate={startLocal.slice(0, 10)}
                        direction={direction === 'BLOCK' ? undefined : direction}
                        initialQuery={direction === 'OUTBOUND' ? referenceNumber : poNumber}
                        onSelect={({ poNumber: picked, clientCode: pickedClient, referenceNumber: pickedRef,
                                     customerCode: pickedCustCode }) => {
                            if (direction === 'OUTBOUND') {
                                // An outbound appointment has no PO. The document
                                // number IS the reference a scheduler tracks it by,
                                // so it lands in Reference Number -- the field they
                                // were typing in when they opened this.
                                setReferenceNumber(picked);
                                if (pickedClient) setClientCode(pickedClient);
                                // Consignee code only when the WMS actually has one.
                                // It is null on most Camelot shipments, and writing a
                                // blank over something the scheduler typed would be
                                // worse than leaving the row alone.
                                if (pickedCustCode) {
                                    setConsignees((prev) => {
                                        const next = [...prev];
                                        next[0] = { ...next[0], consigneeCode: pickedCustCode };
                                        return next;
                                    });
                                }
                                return;
                            }
                            setPoNumber(picked);
                            if (pickedClient) setClientCode(pickedClient);
                            if (pickedRef) setReferenceNumber(pickedRef);
                        }}
                    />

                    {/* Consignees (outbound only). Usually one row; "+ Add"
                        creates additional rows for LTL consolidation. */}
                    {direction === 'OUTBOUND' && (
                        <div className="col-span-2">
                            <Label>Consignee(s)</Label>
                            <div className="mt-1.5 space-y-2">
                                {consignees.map((c, idx) => (
                                    <div key={idx} className="flex gap-2 items-start">
                                        <Input
                                            value={c.consigneeCode}
                                            onChange={(e) => {
                                                const next = [...consignees];
                                                next[idx] = { ...next[idx], consigneeCode: e.target.value.toUpperCase() };
                                                setConsignees(next);
                                            }}
                                            placeholder={idx === 0 ? 'Primary consignee code' : 'Additional consignee code'}
                                            className="font-mono flex-1"
                                        />
                                        <Input
                                            value={c.shipmentNumber}
                                            onChange={(e) => {
                                                const next = [...consignees];
                                                next[idx] = { ...next[idx], shipmentNumber: e.target.value };
                                                setConsignees(next);
                                            }}
                                            placeholder="Shipment # (optional)"
                                            className="font-mono flex-1"
                                        />
                                        {consignees.length > 1 && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => {
                                                    setConsignees(consignees.filter((_, i) => i !== idx));
                                                }}
                                                aria-label="Remove consignee"
                                            >
                                                ×
                                            </Button>
                                        )}
                                    </div>
                                ))}
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setConsignees([...consignees, { consigneeCode: '', shipmentNumber: '' }])}
                                >
                                    + Add another consignee
                                </Button>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1.5">
                                One row per consignee on this appointment. First row is the primary.
                            </p>
                        </div>
                    )}

                    {/* Reference + Pallet (hidden for blocks) */}
                    {direction !== 'BLOCK' && (
                    <>
                    {/* ⚠️ INBOUND ONLY HERE. Outbound renders the same const
                        up in the search slot beside Client - see referenceField. */}
                    {direction !== 'OUTBOUND' && referenceField}
                    <div className="col-span-2 sm:col-span-1">
                        <Label htmlFor="pallets">Pallet Count <span className="text-rose-600">*</span></Label>
                        <Input
                            id="pallets"
                            type="number"
                            min={0}
                            step={1}
                            inputMode="numeric"
                            value={palletCount}
                            onChange={(e) => setPalletCount(e.target.value)}
                            placeholder="e.g. 12"
                            className="mt-1.5 text-tabular"
                        />
                    </div>
                    </>
                    )}


                    {/* Capabilities */}
                    {direction !== 'BLOCK' && lookups.capabilities.length > 0 && (
                        <div className="col-span-2">
                            <Label>Required Capabilities</Label>
                            <div className="mt-1.5 flex flex-wrap gap-2">
                                {lookups.capabilities.map((c) => {
                                    const active = capabilityIds.includes(c.CapabilityId);
                                    return (
                                        <button
                                            type="button"
                                            key={c.CapabilityId}
                                            onClick={() => toggleCapability(c.CapabilityId)}
                                            className={cn(
                                                'px-3 py-1 rounded-full border text-xs font-medium transition-colors',
                                                active
                                                    ? 'bg-primary text-primary-foreground border-primary'
                                                    : 'bg-background hover:bg-accent border-input',
                                            )}
                                        >
                                            {c.Name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Comments / Reason */}
                    <div className="col-span-2">
                        <Label htmlFor="comments">{direction === 'BLOCK' ? 'Reason (optional)' : 'Comments (optional)'}</Label>
                        <Textarea
                            id="comments"
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            placeholder={direction === 'BLOCK' ? 'e.g. Dock maintenance, staging, equipment repair' : 'Special instructions, notes for the dock supervisor, etc.'}
                            className="mt-1.5"
                            rows={2}
                        />
                    </div>

                    {/* Drivers (optional) — only once a carrier is chosen, since
                        the roster is per-carrier. Staged locally and attached
                        after the appointment is created. */}
                    {direction !== 'BLOCK' && carrierCode && (
                        <div className="col-span-2">
                            <Label>Drivers (optional)</Label>
                            <div className="mt-1.5 space-y-2">
                                {stagedDrivers.length > 0 && (
                                    <div className="border rounded-md divide-y">
                                        {stagedDrivers.map((d, i) => (
                                            <div key={`${d.driverId ?? 'new'}-${i}`} className="flex items-center justify-between gap-2 px-3 py-1.5">
                                                <div className="min-w-0">
                                                    <span className="text-sm font-medium">{d.name}</span>
                                                    <span className="text-xs text-muted-foreground"> · {d.phone}</span>
                                                </div>
                                                <button type="button" onClick={() => stageRemoveDriver(i)}
                                                    className="text-xs text-destructive hover:underline shrink-0">Remove</button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div className="flex items-center gap-2 text-sm">
                                    <button type="button" onClick={() => setDriverMode('existing')}
                                        className={cn('px-3 py-1 rounded-md border text-xs', driverMode === 'existing' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-accent border-input')}>
                                        Pick from roster
                                    </button>
                                    <button type="button" onClick={() => setDriverMode('new')}
                                        className={cn('px-3 py-1 rounded-md border text-xs', driverMode === 'new' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-accent border-input')}>
                                        Add new driver
                                    </button>
                                </div>
                                {driverMode === 'existing' && (
                                    <div className="flex items-center gap-2">
                                        {driverRoster === 'loading' && <span className="text-xs text-muted-foreground">Loading roster…</span>}
                                        {driverRoster && !Array.isArray(driverRoster) && driverRoster !== 'loading' && (
                                            <span className="text-xs text-destructive">Couldn&apos;t load roster — {driverRoster.error}</span>
                                        )}
                                        {Array.isArray(driverRoster) && (
                                            <>
                                                <select
                                                    value={pendingRosterId ?? ''}
                                                    onChange={(e) => stageRosterDriver(e.target.value ? Number(e.target.value) : null)}
                                                    className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                                                >
                                                    <option value="">Select a driver…</option>
                                                    {driverRoster
                                                        .filter(d => d.isActive)
                                                        // Already staged drivers leave the list: re-picking one was
                                                        // the only way to reach "That driver is already added",
                                                        // which is an error message for a mistake the UI can just
                                                        // not offer.
                                                        .filter(d => !stagedDrivers.some(sd => sd.driverId === d.driverId))
                                                        .map(d => (
                                                            <option key={d.driverId} value={d.driverId}>{d.name} · {d.phone}</option>
                                                        ))}
                                                </select>
                                            </>
                                        )}
                                    </div>
                                )}
                                {driverMode === 'new' && (
                                    <div className="space-y-2">
                                        <Input value={newDriverName} onChange={(e) => setNewDriverName(e.target.value)} placeholder="Driver name" />
                                        <Input value={newDriverPhone} onChange={(e) => setNewDriverPhone(e.target.value)} placeholder="+1 555 555 5555" />
                                        <Input value={newDriverEmail} onChange={(e) => setNewDriverEmail(e.target.value)} placeholder="Email (optional)" />
                                        <Button type="button" variant="secondary" size="sm" onClick={stageAddDriver}>Add driver</Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {direction !== 'BLOCK' && (
                <>
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none mt-1">
                    <input
                        type="checkbox"
                        checked={notifyCarrier}
                        onChange={(e) => setNotifyCarrier(e.target.checked)}
                        disabled={submitting}
                        className="h-4 w-4 rounded border-input accent-primary"
                    />
                    <span>
                        Email the carrier that this appointment was scheduled
                        {!carrierCode && (
                            <span className="text-muted-foreground"> (select a carrier first)</span>
                        )}
                    </span>
                </label>
                {/**
                  * ---- ✅ DECISION 185 — "EMAIL THE CLIENT", **DISABLED** (2026-09-18) ----
                  *
                  * > Pete: *"This also needs another checkbox. Email the client. So you
                  * > can choose both or neither"* … then, shown that nothing exists to
                  * > send to: *"Leave the line but have it grey'd out so you can't
                  * > select it."*
                  *
                  * ⛔⛔ **THERE IS NO CLIENT EMAIL ADDRESS ANYWHERE IN THE DATABASE.**
                  * Measured 2026-09-18 by asking the catalog for EVERY email-looking
                  * column: 97 clients exist on `dbo.BusinessUnits` and not one has a
                  * contact of any kind. The four `Client*` tables have no email column.
                  * The only object carrying both an email and a Client column is
                  * `dock.Appointment.CreatedByEmail` — **the scheduler, not the client.**
                  *
                  * ⛔ AND THE SERVER WOULD REFUSE ANYWAY. `appointmentStatus.ts:1162`
                  * declares `targets: { carrier: boolean; drivers: boolean }`, and line
                  * 1175 answers **400** to anything else:
                  *     "targets must include 'carrier' and/or 'drivers'."
                  * ⚠️ NewAppointmentDialog's own comment above says *"omitting targets
                  * means BOTH server-side"* — BOTH is carrier + drivers, NOT + client.
                  *
                  *   ** A CHECKBOX THAT CANNOT SEND IS NOT A SMALL FEATURE. IT IS A
                  *      PROMISE THE SCREEN MAKES AND THE SERVER BREAKS, AND NOBODY FINDS
                  *      OUT UNTIL A CLIENT SAYS THEY NEVER GOT THE MAIL. **
                  *
                  * ⭐ Disabled WITH THE REASON ON SCREEN, which is why Pete preferred it
                  * to the alternative he offered (*"leave it and on the confirmation say
                  * no client email exists"*): a control that accepts a click and then
                  * admits it did nothing is worse than one that never accepted it.
                  *
                  * ⬜ **TO MAKE THIS REAL, THREE THINGS ARE NEEDED — none is UI:** a place
                  * to store a client contact, a decision about whose addresses those 97
                  * are (Symbia's or the WMS's), and a `'client'` target plus template on
                  * the notify route. §1AV.158. ⬜ One thread is still open:
                  * `dbo.NotificationContact` is the only email table whose purpose has not
                  * been read — if it holds client contacts keyed some other way, this
                  * checkbox becomes real.
                  *
                  * ⛔ `cursor-not-allowed`, not `cursor-pointer`, and the label is NOT
                  * clickable — a `<label>` wrapping a disabled input still swallows the
                  * click, which reads as an unresponsive control rather than a disabled
                  * one.
                  */}
                <span className="flex items-center gap-2 text-sm select-none mt-1 opacity-60 cursor-not-allowed">
                    <input
                        type="checkbox"
                        checked={false}
                        readOnly
                        disabled
                        aria-describedby="email-client-why"
                        className="h-4 w-4 rounded border-input accent-primary"
                    />
                    <span>
                        Email the client that this appointment was scheduled
                        <span id="email-client-why" className="text-muted-foreground">
                            {' '}(no client contact on file)
                        </span>
                    </span>
                </span>
                </>
                )}

                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button onClick={() => handleSubmit()} disabled={submitting}>
                        {submitting ? (
                            <>
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                Saving…
                            </>
                        ) : (
                            direction === 'BLOCK' ? 'Create block' : 'Create appointment'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---- Helpers ----

/**
 * ⭐ An empty string is not a value. Used for the datetime fields, where a caller
 * spreading `startLocal: ''` must not defeat the default — see the note at the
 * top of the component.
 */
function orDefault(v: string | null | undefined, fallback: () => string): string {
    return (v ?? '').trim().length > 0 ? (v as string) : fallback();
}

/**
 * Default start: the next SLOT boundary, rounded up.
 *
 * ⛔⛔ IT USED TO ROUND TO A HARDCODED 30 MINUTES AND NEVER SAW `slotMinutes`.
 * ⚠️ At a facility configured for 60-minute slots that proposed **09:30** — off
 * the Gantt grid the whole screen is drawn on, and off the increments this
 * dialog's own submit check demands two hundred lines below:
 *
 * > *"Appointments must be at least {slotMinutes} minutes and in
 * >  {slotMinutes}-minute increments"*
 *
 * ⭐⭐ **THE SLOT LENGTH WAS ALREADY AUTHORITATIVE EVERYWHERE EXCEPT THE INPUTS**
 * — the grid snaps to it, the resize handles step by it, the server validates
 * against it. So the fields punished a scheduler at submit for a value the
 * dialog itself had proposed. Corrected 2026-09-13.
 *
 * ⚠️ `slotMinutes` is clamped to a sane floor: a `0` or a negative from a bad
 * config row would make `Math.ceil` divide by zero and produce an Invalid Date,
 * which renders as a blank field — the exact symptom this change exists to end.
 */
function defaultStartLocal(slotMinutes: number = 30): string {
    const mins = Number.isFinite(slotMinutes) && slotMinutes >= 5 ? Math.trunc(slotMinutes) : 30;
    const now = new Date();
    const ms = mins * 60 * 1000;
    const rounded = new Date(Math.ceil(now.getTime() / ms) * ms);
    return toDatetimeLocalString(rounded);
}

/** Default end: one SLOT MIN after start. */
function defaultEndLocal(startLocal: string, slotMinutes: number): string {
    const start = parseDatetimeLocal(startLocal) ?? new Date();
    const end = new Date(start.getTime() + slotMinutes * 60 * 1000);
    return toDatetimeLocalString(end);
}

/** Format a Date as YYYY-MM-DDTHH:mm, the format <input type=datetime-local> wants. */
function toDatetimeLocalString(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Parse the YYYY-MM-DDTHH:mm produced by <input type=datetime-local> as a local-time Date. */
function parseDatetimeLocal(s: string): Date | null {
    if (!s) return null;
    // Browsers without zone suffix interpret as local time, which is what we want.
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
}

/** Convert local-time datetime-local input value to ISO UTC string for the server. */
function localInputToIsoUtc(localStr: string): string | null {
    const d = parseDatetimeLocal(localStr);
    return d ? d.toISOString() : null;
}

function errMsg(err: unknown): string {
    if (err instanceof Error) return err.message;
    return String(err);
}

// Re-export for the parent (used to keep TS happy on import)
export type { PrefillInfo };

// Also referenced indirectly via date-fns import for parseISO usage in derived JSX
void format; void parseISO;
